import { API_HOST } from './config/api'
import { DEFAULT_TRADE_DISPLAY_SYMBOL } from './config/futuresDisplay'
import React, { useEffect, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import { useAppSelector, useAppDispatch } from './hooks/redux'
import { checkAuth, updateUser } from './store/slices/authSlice'
import { useSessionTimeout } from './hooks/useSessionTimeout'
import websocketService from './services/websocketService'

import AdminLoginPage from './pages/AdminLoginPage'
import LoginPage from './pages/LoginPage'

const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-900 flex items-center justify-center">
    <Spin size="large" />
  </div>
)

// 每个懒加载页面独立 Suspense，避免公开路由（如 /admin/login）被全局 loading 挡住
function lazyPage<T extends React.ComponentType<object>>(
  factory: () => Promise<{ default: T }>
) {
  const LazyComponent = React.lazy(factory)
  const Wrapped: React.FC<React.ComponentProps<T>> = (props) => (
    <Suspense fallback={<LoadingSpinner />}>
      <LazyComponent {...props} />
    </Suspense>
  )
  return Wrapped
}

// Pages - 使用懒加载优化性能
const RegisterPage = lazyPage(() => import('./pages/RegisterPage'))
const HomePage = lazyPage(() => import('./pages/HomePage'))
const MarketPage = lazyPage(() => import('./pages/MarketPage'))
const MarketPageMobile = lazyPage(() => import('./pages/MarketPage.mobile'))
const MarketPageSimple = lazyPage(() => import('./pages/MarketPage.simple'))
const TradePage = lazyPage(() => import('./pages/TradePage'))
const SpotTradingPage = lazyPage(() => import('./pages/SpotTradingPage'))
const SecondsTradingPage = lazyPage(() => import('./pages/SecondsTradingPage'))
const MarginTradingPage = lazyPage(() => import('./pages/MarginTradingPage'))
const PortfolioPage = lazyPage(() => import('./pages/PortfolioPage'))
const PriceAlertsPage = lazyPage(() => import('./pages/PriceAlertsPage'))
const CustomerServiceDashboard = lazyPage(() => import('./pages/CustomerServiceDashboard'))
const AdminUserManagement = lazyPage(() => import('./pages/AdminUserManagement'))
const AdminTradingMonitor = lazyPage(() => import('./pages/AdminTradingMonitor'))
const AdminContentManagement = lazyPage(() => import('./pages/AdminContentManagement'))
const AdminSystemConfig = lazyPage(() => import('./pages/AdminSystemConfig'))
const AdminDashboard = lazyPage(() => import('./pages/AdminDashboard'))
const AdminCryptoManagement = lazyPage(() => import('./pages/AdminCryptoManagement'))
const AdminStockManagement = lazyPage(() => import('./pages/AdminStockManagement'))
const AdminWithdrawalManagement = lazyPage(() => import('./pages/AdminWithdrawalManagement'))
const AdminUserVerification = lazyPage(() => import('./pages/AdminUserVerification'))
const AdminUserAssets = lazyPage(() => import('./pages/AdminUserAssets'))
const AdminAssetFlow = lazyPage(() => import('./pages/AdminAssetFlow'))
const TestPage = lazyPage(() => import('./pages/TestPage'))
const AdminDebugPage = lazyPage(() => import('./pages/AdminDebugPage'))
const QuickAdminLogin = lazyPage(() => import('./pages/QuickAdminLogin'))
const ClearStoragePage = lazyPage(() => import('./pages/ClearStoragePage'))
const LoginTestPage = lazyPage(() => import('./pages/LoginTestPage'))
const PortfolioPageSimple = lazyPage(() => import('./pages/PortfolioPage.simple'))
const MinePage = lazyPage(() => import('./pages/MinePage'))
const UserStatusDebug = lazyPage(() => import('./pages/UserStatusDebug'))
const KYCVerificationPage = lazyPage(() => import('./pages/KYCVerificationPage'))
const AdminMarginOrders = lazyPage(() => import('./pages/AdminMarginOrders'))
const AdminSecondsContract = lazyPage(() => import('./pages/AdminSecondsContract'))
const AdminOpenOrders = lazyPage(() => import('./pages/AdminOpenOrders'))
const AdminCloseOrders = lazyPage(() => import('./pages/AdminCloseOrders'))
const AdminPositions = lazyPage(() => import('./pages/AdminPositions'))
const AdminCustomerService = lazyPage(() => import('./pages/AdminCustomerService'))
const AccountRecordsPage = lazyPage(() => import('./pages/AccountRecordsPage'))
const SecurityCenterPage = lazyPage(() => import('./pages/SecurityCenterPage'))
const AboutUsPage = lazyPage(() => import('./pages/AboutUsPage'))
const DepositPage = lazyPage(() => import('./pages/DepositPage'))
const NewsPage = lazyPage(() => import('./pages/NewsPage'))
const NewsDetailPage = lazyPage(() => import('./pages/NewsDetailPage'))
const AdminDepositManagement = lazyPage(() => import('./pages/AdminDepositManagement'))

// Components
import ResponsiveLayout from './components/Layout/ResponsiveLayout'
const AdminLayout = lazyPage(() => import('./components/Layout/AdminLayout'))
import AdminRoute from './components/Auth/AdminRoute'
import PrivateRoute from './components/Auth/PrivateRoute'
import SmartRedirect from './components/Auth/SmartRedirect'
import { LanguageProvider } from './contexts/LanguageContext'
import AntdProvider from './components/Common/AntdProvider'

function SessionTimeoutManager() {
  useSessionTimeout()
  return null
}

function AppContent() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { isAuthenticated, loading } = useAppSelector((state) => state.auth)
  const [isMobile, setIsMobile] = React.useState(false)

  const isPublicRoute = ['/login', '/admin/login', '/register', '/kyc-verification', '/clear-storage', '/login-test', '/quick-admin'].includes(location.pathname)

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    if (isPublicRoute) return

    const token = localStorage.getItem('token')
    if (token) {
      dispatch(checkAuth())
    }

    // 监听用户信息更新事件
    const handleUserUpdated = (event: any) => {
      const updatedUser = event.detail
      dispatch(updateUser(updatedUser))
    }

    window.addEventListener('userUpdated', handleUserUpdated)
    return () => window.removeEventListener('userUpdated', handleUserUpdated)
  }, [dispatch, isPublicRoute])

  useEffect(() => {
    if (isAuthenticated) {
      try {
        websocketService.connect()
      } catch (error) {
        console.error('WebSocket connection failed:', error)
      }
    } else {
      websocketService.disconnect()
    }
  }, [isAuthenticated])

  // 定期检查用户状态更新（例如 KYC 认证状态）
  useEffect(() => {
    if (!isAuthenticated) return

    const checkUserStatus = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) return

        const response = await fetch(API_HOST + '/api/auth/verify', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        // Token 过期，自动退出登录
        if (response.status === 401) {
          console.warn('Token expired, logging out...')
          const { logout } = await import('./store/slices/authSlice')
          dispatch(logout())
          window.location.href = '/login'
          return
        }

        if (response.ok) {
          const data = await response.json()
          if (data.success && data.data.user) {
            // 更新用户信息到 Redux store
            const { updateUser } = await import('./store/slices/authSlice')
            dispatch(updateUser({
              kycStatus: data.data.user.kycStatus,
              accountType: data.data.user.accountType,
              name: data.data.user.name
            }))
          }
        }
      } catch (error) {
        console.error('Failed to check user status:', error)
      }
    }

    // 立即检查一次
    checkUserStatus()

    // 每30秒检查一次
    const interval = setInterval(checkUserStatus, 30000)

    // 当页面重新获得焦点时也检查一次
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkUserStatus()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, dispatch])

  if (loading && !isPublicRoute) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    )
  }

  return (
    <LanguageProvider>
      <AntdProvider>
      <SessionTimeoutManager />
      <div className="App">
          <Routes>
            {/* 公开路径 */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/kyc-verification" element={<KYCVerificationPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/quick-admin" element={<QuickAdminLogin />} />
            <Route path="/clear-storage" element={<ClearStoragePage />} />
            <Route path="/login-test" element={<LoginTestPage />} />
            
            {/* 需要认证的路径 */}
            <Route path="/home" element={<PrivateRoute><ResponsiveLayout><HomePage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/market" element={<PrivateRoute><ResponsiveLayout>{isMobile ? <MarketPageMobile /> : <MarketPage />}</ResponsiveLayout></PrivateRoute>} />
            <Route path="/market-mobile" element={<PrivateRoute><ResponsiveLayout><MarketPageMobile /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/market-simple" element={<PrivateRoute><ResponsiveLayout><MarketPageSimple /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/test" element={<PrivateRoute><ResponsiveLayout><TestPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/admin-debug" element={<PrivateRoute><ResponsiveLayout><AdminDebugPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/trade" element={<Navigate to={`/trade/${DEFAULT_TRADE_DISPLAY_SYMBOL}`} replace />} />
            <Route path="/trade/:symbol" element={<PrivateRoute><ResponsiveLayout><TradePage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/spot-trading" element={<PrivateRoute><ResponsiveLayout><SpotTradingPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/spot-trading/:symbol" element={<PrivateRoute><ResponsiveLayout><SpotTradingPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/seconds-trading" element={<PrivateRoute><ResponsiveLayout><SecondsTradingPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/seconds-trading/:symbol" element={<PrivateRoute><ResponsiveLayout><SecondsTradingPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/margin-trading" element={<PrivateRoute><ResponsiveLayout><MarginTradingPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/margin-trading/:symbol" element={<PrivateRoute><ResponsiveLayout><MarginTradingPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/portfolio" element={<PrivateRoute><ResponsiveLayout><PortfolioPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/portfolio-simple" element={<PrivateRoute><ResponsiveLayout><PortfolioPageSimple /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/alerts" element={<PrivateRoute><ResponsiveLayout><PriceAlertsPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/customer-service" element={<PrivateRoute><ResponsiveLayout><CustomerServiceDashboard /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/mine" element={<PrivateRoute><ResponsiveLayout><MinePage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/account-records" element={<PrivateRoute><ResponsiveLayout><AccountRecordsPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/deposit" element={<PrivateRoute><ResponsiveLayout><DepositPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/news" element={<PrivateRoute><ResponsiveLayout><NewsPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/news/:id" element={<PrivateRoute><ResponsiveLayout><NewsDetailPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/security-center" element={<PrivateRoute><ResponsiveLayout><SecurityCenterPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/about-us" element={<PrivateRoute><ResponsiveLayout><AboutUsPage /></ResponsiveLayout></PrivateRoute>} />
            <Route path="/debug/user-status" element={<PrivateRoute><ResponsiveLayout><UserStatusDebug /></ResponsiveLayout></PrivateRoute>} />
            
            {/* 后台管理路由 */}
            <Route path="/admin/dashboard" element={<AdminRoute><AdminLayout><AdminDashboard /></AdminLayout></AdminRoute>} />
            <Route path="/admin/crypto/current" element={<AdminRoute><AdminLayout><AdminCryptoManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/crypto/history" element={<AdminRoute><AdminLayout><AdminCryptoManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/stock/spot" element={<AdminRoute><AdminLayout><AdminStockManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/stock/margin" element={<AdminRoute><AdminLayout><AdminStockManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/stock/settings" element={<AdminRoute><AdminLayout><AdminStockManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/stock/margin-orders" element={<AdminRoute><AdminLayout><AdminMarginOrders /></AdminLayout></AdminRoute>} />
            <Route path="/admin/stock/seconds-contract" element={<AdminRoute><AdminLayout><AdminSecondsContract /></AdminLayout></AdminRoute>} />
            <Route path="/admin/stock/open-orders" element={<AdminRoute><AdminLayout><AdminOpenOrders /></AdminLayout></AdminRoute>} />
            <Route path="/admin/stock/close-orders" element={<AdminRoute><AdminLayout><AdminCloseOrders /></AdminLayout></AdminRoute>} />
            <Route path="/admin/stock/positions" element={<AdminRoute><AdminLayout><AdminPositions /></AdminLayout></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminLayout><AdminUserManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/users/verification" element={<AdminRoute><AdminLayout><AdminUserVerification /></AdminLayout></AdminRoute>} />
            <Route path="/admin/users/assets" element={<AdminRoute><AdminLayout><AdminUserAssets /></AdminLayout></AdminRoute>} />
            <Route path="/admin/users/flow" element={<AdminRoute><AdminLayout><AdminAssetFlow /></AdminLayout></AdminRoute>} />
            <Route path="/admin/users/withdrawal" element={<AdminRoute><AdminLayout><AdminWithdrawalManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/users/deposit" element={<AdminRoute><AdminLayout><AdminDepositManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/trading" element={<AdminRoute><AdminLayout><AdminTradingMonitor /></AdminLayout></AdminRoute>} />
            <Route path="/admin/customer-service" element={<AdminRoute><AdminLayout><AdminCustomerService /></AdminLayout></AdminRoute>} />
            <Route path="/admin/content" element={<AdminRoute><AdminLayout><AdminContentManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/config" element={<AdminRoute><AdminLayout><AdminSystemConfig /></AdminLayout></AdminRoute>} />
            
            {/* 根路径智能重定向 */}
            <Route path="/" element={isAuthenticated ? <SmartRedirect /> : <Navigate to="/login" replace />} />
          </Routes>
      </div>
      </AntdProvider>
    </LanguageProvider>
  )
}

export default AppContent
